/**
 * @fileoverview Non-intrusive bottom notice bar describing our use
 * of browser localStorage.
 *
 * Why this is a notice and not a consent banner: we have no analytics,
 * no advertising, no third-party trackers, and we set no HTTP cookies
 * at all. The first-party `localStorage` we use exists solely to
 * support a feature the user has explicitly requested (remembering
 * their wallet so they don't have to paste a mnemonic on every page).
 * Under the EU ePrivacy Directive and GDPR, "strictly necessary"
 * storage of that kind does not require an opt-in pop-up; it just
 * requires informing the user.
 *
 * The bar therefore prioritises:
 *   - calmness: it sits at the bottom, doesn't dim the page, doesn't
 *     block scrolling or clicks, and does not gate access to anything;
 *   - transparency: a single short sentence with a link to the full
 *     Privacy Notice for the inquisitive;
 *   - persistence of choice: once dismissed, it stays dismissed until
 *     the user clears site data (a counter is bumped in
 *     `secresea_notice_dismissed_v1` so we can re-show the bar after
 *     material changes).
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'

import { zcashWallet } from '../../services/zcashWallet'

const DISMISS_KEY = 'secresea_notice_dismissed_v1'

// The notice is informational, not a consent gate, so it shouldn't camp at
// the bottom of the screen. It fades itself out after a short read window and
// immediately steps aside for the wallet status bar when a scan starts.
const AUTO_HIDE_MS = 12000

function readDismissedFlag(): boolean {
	if (typeof window === 'undefined') return true
	try {
		return window.localStorage.getItem(DISMISS_KEY) === 'true'
	} catch {
		return true
	}
}

export function StorageNoticeBar() {
	const [visible, setVisible] = useState(false)

	useEffect(() => {
		if (!readDismissedFlag()) {
			setVisible(true)
		}
	}, [])

	// Auto-hide after the read window. We deliberately do NOT persist the
	// permanent dismissal here, so a future visit still surfaces the notice
	// once until the user explicitly clicks "Got it".
	useEffect(() => {
		if (!visible) return
		const timer = setTimeout(() => setVisible(false), AUTO_HIDE_MS)
		return () => clearTimeout(timer)
	}, [visible])

	// Yield the bottom strip to the wallet status bar the moment a scan or
	// send begins — two stacked bars would be noisy and overlap.
	useEffect(() => {
		return zcashWallet.onScanProgress((progress) => {
			if (progress.isScanning) setVisible(false)
		})
	}, [])

	// Also step aside whenever a wallet is connected: the wallet bar then keeps
	// a persistent "Connected" strip in the same bottom slot, so this notice
	// would otherwise sit behind it.
	useEffect(() => {
		const yieldIfConnected = () => {
			try {
				if (zcashWallet.isConnected()) setVisible(false)
			} catch {
				/* ignore */
			}
		}
		yieldIfConnected()
		return zcashWallet.onConnectionChange(yieldIfConnected)
	}, [])

	const handleDismiss = () => {
		try {
			window.localStorage.setItem(DISMISS_KEY, 'true')
		} catch {
			/* private mode / quota — best effort, just hide */
		}
		setVisible(false)
	}

	if (!visible) return null

	return (
		<div
			role="region"
			aria-label="Browser storage notice"
			className="fixed inset-x-0 bottom-0 z-40 bg-gray-900/95 text-gray-100 text-sm shadow-lg backdrop-blur"
		>
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
				<p className="leading-relaxed">
					<span className="font-semibold">No cookies, no tracking.</span>{' '}
					By default Secresea keeps your wallet only for this browser{' '}
					<span className="font-semibold">session</span> (it survives page
					reloads but is cleared when you close the tab). You can optionally
					protect it with a password, or
					choose to remember it on this device{' '}
					<span className="font-semibold">encrypted</span>. Nothing here is
					sent to our servers.{' '}
					<Link
						to="/privacy"
						className="underline underline-offset-2 text-zcash-300 hover:text-zcash-200"
					>
						Read the Privacy Notice
					</Link>
					.
				</p>
				<div className="flex items-center gap-2 self-end sm:self-auto">
					<button
						type="button"
						onClick={handleDismiss}
						className="inline-flex items-center gap-1 rounded-md border border-zcash-300/50 px-3 py-1.5 text-zcash-100 hover:bg-zcash-800/40"
					>
						<span>Got it</span>
						<XMarkIcon className="h-4 w-4" aria-hidden="true" />
					</button>
				</div>
			</div>
		</div>
	)
}

export default StorageNoticeBar
